<template>
    <div class="ai-knowledge">
        <div class="header">
            <span class="story-name">{{ storyTitle }} </span>
            <div class="search-wrapper">
                <div class="tpl-search">
                    <i class="material-icons">search</i>
                    <input type="text" :placeholder="$t('message.search')" />
                </div>
            </div>
            <IconButton :on-click="() => $emit('close')">
                <i class="material-icons">close</i>
            </IconButton>
        </div>
        <div class="page-content">
            <div class="sidebar">
                <div class="first">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center gap-5">
                            <i class="material-icons">auto_awesome</i>
                            <strong>{{ $t("chatbotv2.aiKnowledge") }}</strong>
                        </div>

                        <IconButton>
                            <i class="material-icons">help_outline</i>
                        </IconButton>
                    </div>

                    <div class="menu-items">
                        <div
                            class="menu-item"
                            :class="{ selected: currentPage === 'websites' }"
                            @click="currentPage = 'websites'"
                        >
                            <span>{{ $t("chatbotv2.websites") }}</span>
                            <span class="value">{{ knowledgeBaseTotal.webpage }}</span>
                        </div>
                        <div
                            class="menu-item"
                            :class="{ selected: currentPage === 'articles' }"
                            @click="currentPage = 'articles'"
                        >
                            {{$t("chatbotv2.articles")}}
                            <span class="value">{{ knowledgeBaseTotal.article }}</span>
                        </div>
                        <div
                            class="menu-item"
                            :class="{ selected: currentPage === 'faq' }"
                            @click="currentPage = 'faq'"
                        >
                            {{$t("chatbotv2.faq")}}
                            <span class="value">{{ knowledgeBaseTotal.faq }}</span>
                        </div>
                        <div
                            class="menu-item"
                            :class="{ selected: currentPage === 'files' }"
                            @click="currentPage = 'files'"
                        >
                            {{$t("chatbotv2.files")}}
                            <span class="value">{{ knowledgeBaseTotal.file }}</span>
                        </div>
                        <div
                            class="menu-item"
                            :class="{ selected: currentPage === 'functions' }"
                            @click="currentPage = 'functions'"
                        >
                            {{$t("chatbotv2.functions")}}
                            <span class="value">{{ knowledgeBaseTotal.aiFunction }}</span>
                        </div>
                        <div
                            class="menu-item"
                            :class="{ selected: currentPage === 'settings' }"
                            @click="currentPage = 'settings'"
                        >
                            {{$t("chatbotv2.settings")}}
                            <span class="value">{{ files.length }}</span>
                        </div>
                    </div>
                </div>
                <div class="second">
                    <div class="d-flex align-items-center gap-5">
                        <IconButton>
                            <i class="material-icons">help_outline</i>
                        </IconButton>
                        <strong>{{$t("chatbotv2.modalUsage")}}</strong>
                    </div>
                    <div class="stats">
                        <div class="value">
                            <strong>{{
                                knowledgeBaseTotal.article +
                                knowledgeBaseTotal.faq +
                                knowledgeBaseTotal.webpage +
                                knowledgeBaseTotal.file +
                                knowledgeBaseTotal.aiFunction
                            }}</strong>
                            <span>/ 1000</span>
                        </div>
                        <div class="description">{{ $t("chatbotv2.usedContentSources") }}</div>
                    </div>
                </div>
            </div>

            <div class="content" v-if="currentPage === 'websites'">
                <Websites :storyId="storyId" @trained="trained" />
            </div>
            <div class="content" v-else-if="currentPage === 'articles'">
                <Articles :storyId="storyId" />
            </div>
            <div class="content" v-else-if="currentPage === 'faq'">
                <FaqList :story-id="storyId" />
            </div>
            <div class="content" v-else-if="currentPage === 'files'">
                <KBFiles :story-id="storyId" @updated="(items) => (files = items)" />
            </div>
            <div class="content" v-else-if="currentPage === 'functions'">
                <FunctionList :story-id="storyId" @updated="(items) => (files = items)" />
            </div>
            <div class="content" v-else-if="currentPage === 'settings'">
                <ChatbotAiPrompt :storyId="storyId" />
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import IconButton from "../../Settings/components/IconButton.vue";
import Websites from "./Websites.vue";
import { CHATBOT_API } from "../../../../api/chatbotv2";
import ComingSoon from "./ComingSoon.vue";
import KBFiles from "./files/KBFiles.vue";
import ChatbotAiPrompt from "./files/ChatbotAiPrompt.vue";
import Articles from "./files/Articles.vue";
import FaqList from "./files/FaqList.vue";
import FunctionList from "./function/FunctionList.vue";

export default Vue.extend({
    name: "AIKnowledge",
    components: {
        FunctionList,
        KBFiles,
        Websites,
        IconButton,
        ComingSoon,
        ChatbotAiPrompt,
        Articles,
        FaqList,
    },
    props: {
        storyId: String,
        storyTitle: String,
    },
    data() {
        return {
            currentPage: "websites",
            knowledgeBases: [],
            files: [],
            total: 0,
            loading: true,
            knowledgeBaseTotal: { webpage: 0, file: 0, faq: 0, article: 0, aiFunction: 0 },
        };
    },
    methods: {
        trained() {
            this.fetchKnowledgeBases();
        },
        fetchKnowledgeBases() {
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBaseLite(this.storyId)
                .then((response) => {
                    const { data, total } = response.data;
                    this.knowledgeBases = data;
                    this.total = total;
                    this.loading = false;
                })
                .finally(() => {
                    this.loading = false;
                });
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "file").then((res) => {
                this.files = res.data;
            });
        },
        async fetchKnowledgeBaseCountByType() {
            try {
                const res = await CHATBOT_API.KNOWLEDGE_BASE.knowledgeBaseCountByType(this.storyId);
                console.log("Knowledge base count by type:", res.data);
                this.knowledgeBaseTotal = {
                    webpage: res.data.webpage || 0,
                    file: res.data.file || 0,
                    faq: res.data.faq || 0,
                    article: res.data.article || 0,
                    aiFunction: res.data.aiFunction || 0,
                };
            } catch (error) {
                console.error("Error fetching knowledge base count by type:", error);
            }
        },
    },
    mounted() {
        this.fetchKnowledgeBases();
        this.fetchKnowledgeBaseCountByType();
    },
});
</script>

<style scoped lang="scss">
.ai-knowledge {
    width: 100%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 25px 25px;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 999;
    //border: 1px solid red;
    background-color: #f6f6f6;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 30px;

        .story-name {
            color: #22272d;
            cursor: pointer;
            display: block;
            font-size: 18px;
            font-weight: 700;
            height: 50px;
            line-height: 50px;
            overflow: hidden !important;
            position: relative;
            text-overflow: ellipsis;
            -webkit-transition: all 0.3s;
            transition: all 0.3s;
            white-space: nowrap;
        }

        .search-wrapper {
            height: 100%;

            .tpl-search {
                position: relative;
                width: 100%;
            }

            .material-icons {
                color: #979797;
                font-size: 20px;
                left: 20px;
                top: 14px;
                position: absolute;
            }

            input {
                background: #fff;
                border: 1px solid #fff;
                border-radius: 10px;
                -webkit-box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
                box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
                color: #22272d;
                font-size: 16px;
                height: 48px;
                line-height: 20px;
                padding-left: 52px;
                padding-right: 20px;
                -webkit-transition: border 0.3s;
                transition: border 0.3s;
                width: 100%;
                -webkit-appearance: none;
            }
        }

        .icon-button {
            border-radius: 5px;
            background-color: #fff;
            width: 35px;
            height: 35px;
            box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);

            &:hover {
                opacity: 0.8;
            }
        }
    }

    .page-content {
        display: flex;
        height: calc(100% - 80px);

        .sidebar {
            background: #fff;
            //border-radius: 15px;
            box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
            display: grid;
            grid-template-rows: auto minmax(0, 1fr) auto;
            padding: 36px;
            width: 350px;
            height: 100%;
            //min-height: calc(100% - 69px);
            //max-height: 100%;
            border-right: 1px solid #e5e5e5;

            .first {
                display: flex;
                flex-direction: column;
                gap: 10px;

                strong {
                    color: #22272d;
                    font-size: 22px;
                    font-weight: 600;
                    line-height: 28px;
                }
            }

            .second {
                display: flex;
                flex-direction: column;
                gap: 10px;
                justify-content: flex-end;

                strong {
                    color: #22272d;
                    font-size: 22px;
                    font-weight: 600;
                    line-height: 28px;
                }

                .stats {
                    background: var(--color-bg-neutral-default);
                    border-radius: 10px;
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    align-items: center;
                    justify-content: center;

                    strong {
                        font-size: 40px;
                        font-weight: 700;
                        line-height: 34px;
                        margin-right: 5px;
                    }
                }
            }

            .menu-items {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .menu-item {
                    padding: 10px 15px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                    border-radius: 5px;

                    .value {
                    }

                    &.selected {
                        background: #22272d;
                        color: var(--color-text-onprimary-selected);
                    }

                    &:hover {
                        background: #22272d;
                        color: var(--color-text-onprimary-selected);
                    }
                }
            }
        }

        .content {
            width: calc(100% - 350px);
            height: 100%;
            display: flex;
            flex-direction: column;
            background: #fff;
            border-radius: 0 15px 15px 0;
        }
    }
}
</style>
